import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withProps, withState, } from '@ngrx/signals';
import { injectDispatch } from '@ngrx/signals/events';
import { withActionTrackers } from '@app/shared/store/with-action-tracker';
import { AuthApi } from '../auth-api';
import { KeycloakAuthClient, MAX_SESSION_MS } from '../keycloak-auth-client';
import { ToastManager } from '../../toasts/toast-manager';
import { AuthEvents } from './auth-events';
import { AuthState } from './auth-state';
import { withAuthEventHandlers } from './with-auth-event-handlers';

const actions = [
    'changePassword',
    'confirmRegistration',
    'createAccount',
    'login',
    'logout',
    'resetPassword',
] as const;
export type AuthAction = typeof actions[number];

export const AuthStore = signalStore(
    { providedIn: 'root' },
    withState<AuthState>({
        user: null,
        mfaRequired: false,
    }),
    withProps(() => ({
        _authClient: inject(KeycloakAuthClient),
        _authApi: inject(AuthApi),
        _toastManager: inject(ToastManager),
    })),
    withActionTrackers(...actions),
    withAuthEventHandlers(),
    withMethods((store) => {
        return {
            /**
             * Clears the cached user state without redirecting. Used when the envelope
             * viewer detects a disabled account and needs to sign out before navigating
             * to registration, so that the redirectIfAuthenticated guard sees a null user.
             */
            clearUser(): void {
                store._authClient.logout();
                patchState(store, { user: null, mfaRequired: false });
            },

            /**
             * Resolves once the auth state is ready. The Keycloak session is restored
             * synchronously from storage, so this resolves immediately.
             */
            async authStateReady(): Promise<void> {
                return Promise.resolve();
            },

            /**
             * Changes the password of the logged-in user (reauthenticates server-side with the
             * current password, then sets the new one).
             * @param currentPassword The current password.
             * @param newPassword The new password.
             */
            async changePassword(currentPassword: string, newPassword: string): Promise<void> {
                await store._authApi.changePassword(currentPassword, newPassword)
                    .then((): void => {
                        store._toastManager.success(
                            $localize`Done!`,
                            $localize`Your password has been successfully changed.`,
                        );
                    })
                    .catch((error: unknown): Promise<never> => {
                        store._toastManager.error(
                            $localize`Password changing failed!`,
                            $localize`Please try again later.`,
                        );
                        return Promise.reject(error instanceof Error ? error : new Error('Password change failed'));
                    });
            },

            /**
             * Verifies a password reset code that has previously been sent to a user.
             * @param code The target code.
             * @throws If the code is invalid.
             */
            async verifyPasswordResetCode(code: string): Promise<void> {
                await store._authApi.verifyResetCode(code);
            },
        };
    }),
    withHooks((store, dispatcher = injectDispatch(AuthEvents)) => {
        let logoutTimer: ReturnType<typeof setTimeout> | undefined;

        const scheduleSessionExpiry = (): void => {
            clearTimeout(logoutTimer);
            const authTimeMs: number | null = store._authClient.authTimeMs();
            if (authTimeMs === null) {
                return;
            }
            const expiresInMs: number = authTimeMs + MAX_SESSION_MS - Date.now();
            if (expiresInMs <= 0) {
                dispatcher.logout({ redirect: '/auth/login' });
                return;
            }
            logoutTimer = setTimeout(
                (): void => dispatcher.logout({ redirect: '/auth/login' }),
                expiresInMs,
            );
        };

        return {
            onInit(): void {
                if (store._authClient.isAuthenticated()) {
                    const user = store._authClient.currentUser();
                    if (user) {
                        dispatcher.loginSuccess({ user, redirect: '' });
                    }
                    scheduleSessionExpiry();
                } else {
                    // Stale/expired session in storage — clear it.
                    store._authClient.logout();
                }
            },
        };
    }),
);

export type AuthStore = InstanceType<typeof AuthStore>;
